// frontend/src/auth/postAuthRedirect.ts
// Task 4.2: Deep-Links ueberleben den Login-Umweg.
//
// Bis hierher hat RequireAuth zwar brav `state={{ from: location }}` mitgegeben,
// aber niemand hat es gelesen — Login, Onboarding-Chat und der Server sprangen
// alle hart auf /dashboard. Wer per Push/Universal-Link/Bookmark auf /coins kam
// und nicht eingeloggt war, landete danach auf dem Dashboard.
//
// Rangfolge des Ziels (bewusst fix, damit alle Einstiege gleich entscheiden):
//   1. location.state.from — von RequireAuth beim Gast-Redirect gesetzt. Das
//      praeziseste Signal, weil es die komplette SPA-Location (inkl. hash) haelt.
//   2. ?next= der aktuellen Location — kommt vom Server: die
//      PROTECTED_HTML_PAGES-Middleware haengt unter WEB_SPA das Original-Ziel an,
//      statt es beim 302 auf '/' zu verlieren (server/index.js).
//   3. sessionStorage-Merker — Notnagel fuer den Onboarding-Umweg: der
//      Chat-Flow navigiert nach Login/Register hart auf /dashboard und verliert
//      dabei Router-State UND Query. RequireAuth legt das Ziel deshalb beim
//      Gast-Redirect ab und loest es beim Landen auf /dashboard wieder ein.
//
// Sicherheit: jedes Ziel laeuft durch `isSafeAppPath` (gleiche Regeln wie die
// nativen Deep-Links) — nur App-interne Pfade, kein '//host', kein Schema, kein
// '..'. Zusaetzlich fallen /login und /onboarding raus, sonst schickt der Login
// den Nutzer im Kreis.

import type { AuthUser, FavoriteTab } from '../api/types';
import { isSafeAppPath } from '../native/deepLinks';
import { readPref, writePref } from '../shared/clientPrefs';

// ── Lieblings-Tab (E13/W1-C, Karte C Empfehlung a) ──────────────────────────
// Wo landet die App nach dem Start? Bis hierher war das hart '/dashboard' an
// DREI Stellen (routes.tsx-Index, dieser Fallback, RequireAuth-Landeplatz) —
// Karte C Risiko 5: wer nur eine davon anfasst, bekommt entweder ein Blinken
// (Doppel-Redirect) oder einen Deep-Link-Merker, der den ersten regulaeren
// Besuch des neuen Tabs kapert. Deshalb liegt die Zuordnung Tab -> Pfad jetzt
// EINMAL hier, und alle drei Stellen lesen sie.
//
// Wahrheit ist der Server (`users.favorite_tab` im /me-Payload, W1-A). Der
// clientPrefs-Spiegel (Cookie + localStorage, dasselbe Dual-Write-Muster wie
// Theme/Font) ist nur die Beschleunigung fuer den Kaltstart: er routet den
// ERSTEN Render richtig, bevor /api/auth/me geantwortet hat. Sobald die
// Antwort da ist, zieht `syncFavoriteTabMirror` den Spiegel nach.
export const FAVORITE_TAB_MIRROR_KEY = 'lorath_favorite_tab';

/** Server-Default (Migration: `DEFAULT 'adventures'`) — E15, kein Backfill. */
export const DEFAULT_FAVORITE_TAB: FavoriteTab = 'adventures';

/** Die EINE Zuordnung Lieblings-Tab -> Landeplatz. */
const FAVORITE_TAB_PATHS: Record<FavoriteTab, string> = {
  chats: '/chats',
  adventures: '/dashboard',
  discover: '/discover',
};

/** Alle moeglichen Landeplaetze — RequireAuth muss sie ALLE kennen. */
export const FAVORITE_TAB_LANDING_PATHS: readonly string[] = Object.freeze(
  Object.values(FAVORITE_TAB_PATHS),
);

/** Ziel, wenn nichts (Gueltiges) gemerkt wurde — der Lieblings-Tab-Default. */
export const POST_AUTH_FALLBACK_PATH = FAVORITE_TAB_PATHS[DEFAULT_FAVORITE_TAB];

function isFavoriteTab(value: unknown): value is FavoriteTab {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(FAVORITE_TAB_PATHS, value);
}

/** Gespiegelter Wert vom letzten Start (Cookie/localStorage) — oder null. */
export function readFavoriteTabMirror(): FavoriteTab | null {
  try {
    const raw = readPref(FAVORITE_TAB_MIRROR_KEY);
    return isFavoriteTab(raw) ? raw : null;
  } catch {
    // Kein document/Storage (SSR, Privatmodus) — der Spiegel ist best-effort.
    return null;
  }
}

/** Spiegel schreiben (Dual-Write Cookie + localStorage, wie Theme/Font). */
export function writeFavoriteTabMirror(tab: FavoriteTab): void {
  try {
    writePref(FAVORITE_TAB_MIRROR_KEY, tab);
  } catch {
    // Best-effort: ohne Spiegel routet der naechste Kaltstart auf den Default.
  }
}

interface FavoriteTabSource {
  favorite_tab?: unknown;
}

/**
 * Der geltende Lieblings-Tab: Server zuerst (Wahrheit), sonst der Spiegel
 * (Kaltstart vor der /me-Antwort), sonst der Default.
 */
export function favoriteTabOf(user?: FavoriteTabSource | AuthUser | null): FavoriteTab {
  const fromServer = user?.favorite_tab;
  if (isFavoriteTab(fromServer)) return fromServer;
  return readFavoriteTabMirror() ?? DEFAULT_FAVORITE_TAB;
}

/**
 * Der Landeplatz des Lieblings-Tabs. Loest den frueheren harten
 * POST_AUTH_FALLBACK_PATH an allen drei Startroute-Stellen ab.
 */
export function favoriteTabPath(user?: FavoriteTabSource | AuthUser | null): string {
  return FAVORITE_TAB_PATHS[favoriteTabOf(user)];
}

/**
 * Spiegel nachziehen, sobald der Server geantwortet hat. Schreibt NUR bei einem
 * gueltigen Serverwert und nur bei echter Aenderung (kein Cookie-Geschreibe bei
 * jedem Render).
 */
export function syncFavoriteTabMirror(user?: FavoriteTabSource | AuthUser | null): void {
  const fromServer = user?.favorite_tab;
  if (!isFavoriteTab(fromServer)) return;
  if (readFavoriteTabMirror() === fromServer) return;
  writeFavoriteTabMirror(fromServer);
}

/** true fuer jeden moeglichen Lieblings-Tab-Landeplatz. */
export function isFavoriteTabLandingPath(pathname: string): boolean {
  return FAVORITE_TAB_LANDING_PATHS.includes(pathname);
}

const PENDING_REDIRECT_KEY = 'lorath_post_auth_redirect';
// Der Merker ist eine Kruecke ueber genau EINEN Auth-Vorgang. Laeuft er ab,
// bevor der Nutzer fertig ist (Onboarding-Registrierung dauert), faellt er
// still auf /dashboard zurueck — besser als ein Ziel aus der Vorwoche.
const PENDING_TTL_MS = 30 * 60 * 1000;

// Pfade, die als Ziel nichts taugen: '/' und die drei Lieblings-Tab-Landeplaetze
// sind ohnehin das Ziel des Startroute-Redirects, '/login' und '/onboarding'
// wuerden den Nutzer nach dem Login zurueck in den Login schicken.
//
// Warum ALLE Landeplaetze und nicht nur der aktuelle Lieblings-Tab: der Merker
// wird ausschliesslich AUF einem Landeplatz eingeloest (RequireAuth). Waere
// '/chats' dort ein gueltiges Ziel, gewaenne beim Landen der Pfad selbst gegen
// den gemerkten Deep-Link — der Deep-Link ginge verloren. Preis: ein Deep-Link
// auf einen NICHT gewaehlten Tab landet auf dem Lieblings-Tab; das ist genau
// das bisherige Verhalten fuer '/dashboard' und einen Tipp in der Leiste weit.
const BLOCKED_TARGET_PATHS = new Set([
  '/',
  '/login',
  '/onboarding',
  ...FAVORITE_TAB_LANDING_PATHS,
]);

export interface RedirectLocationLike {
  pathname?: unknown;
  search?: unknown;
  hash?: unknown;
  state?: unknown;
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    // Privater Modus / Storage-Disable — der Merker ist best-effort.
    return null;
  }
}

/**
 * Prueft ein Redirect-Ziel und liefert es normalisiert zurueck (oder null).
 * Akzeptiert nur App-interne Pfade; Endlosschleifen-Ziele wie /login werden
 * bewusst verworfen, damit der Aufrufer auf POST_AUTH_FALLBACK_PATH faellt.
 */
export function sanitizeRedirectTarget(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value || !isSafeAppPath(value)) return null;
  const pathname = value.split(/[?#]/, 1)[0];
  if (BLOCKED_TARGET_PATHS.has(pathname)) return null;
  return value;
}

function targetFromSearch(search: unknown): string | null {
  if (typeof search !== 'string' || !search) return null;
  try {
    return sanitizeRedirectTarget(new URLSearchParams(search).get('next'));
  } catch {
    return null;
  }
}

/**
 * Ziel aus einer Location: erst der Pfad selbst (der Gast stand ja schon auf
 * dem Deep-Link), sonst der vom Server angehaengte ?next=.
 */
export function targetFromLocation(location: RedirectLocationLike | null | undefined): string | null {
  if (!location) return null;
  const { pathname, search, hash } = location;
  if (typeof pathname === 'string') {
    const suffix = `${typeof search === 'string' ? search : ''}${typeof hash === 'string' ? hash : ''}`;
    const direct = sanitizeRedirectTarget(`${pathname}${suffix}`);
    if (direct) return direct;
  }
  return targetFromSearch(search);
}

function targetFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null;
  const from = (state as { from?: unknown }).from;
  if (typeof from === 'string') return sanitizeRedirectTarget(from);
  if (!from || typeof from !== 'object') return null;
  return targetFromLocation(from as RedirectLocationLike);
}

/** Merker setzen (null loescht ihn) — beim Gast-Redirect in RequireAuth. */
export function rememberPostAuthRedirect(target: string | null): void {
  const storage = getSessionStorage();
  if (!storage) return;
  const safe = sanitizeRedirectTarget(target);
  try {
    if (!safe) {
      storage.removeItem(PENDING_REDIRECT_KEY);
      return;
    }
    storage.setItem(PENDING_REDIRECT_KEY, JSON.stringify({ path: safe, ts: Date.now() }));
  } catch {
    // Quota / SecurityError — ohne Merker landet der Nutzer auf /dashboard.
  }
}

/** Gemerktes Ziel lesen (abgelaufene/kaputte Eintraege gelten als "keins"). */
export function readPostAuthRedirect(): string | null {
  const storage = getSessionStorage();
  if (!storage) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(PENDING_REDIRECT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { path?: unknown; ts?: unknown };
    if (typeof parsed?.ts !== 'number' || Date.now() - parsed.ts > PENDING_TTL_MS) return null;
    return sanitizeRedirectTarget(parsed.path);
  } catch {
    return null;
  }
}

export function clearPostAuthRedirect(): void {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(PENDING_REDIRECT_KEY);
  } catch {
    // Best-effort.
  }
}

/** Ziel nach der Rangfolge oben — null, wenn es keins gibt. */
export function resolvePostAuthTarget(location: RedirectLocationLike | null | undefined): string | null {
  return targetFromState(location?.state) ?? targetFromLocation(location) ?? readPostAuthRedirect();
}

/**
 * Wie resolvePostAuthTarget, aber immer mit Ziel. Fallback ist der Lieblings-Tab
 * (Server-Wert, sonst Spiegel, sonst '/dashboard').
 */
export function resolvePostAuthRedirect(
  location: RedirectLocationLike | null | undefined,
  user?: FavoriteTabSource | AuthUser | null,
): string {
  return resolvePostAuthTarget(location) ?? favoriteTabPath(user);
}
